import React, { useMemo, useState } from 'react'
import { endOfDay, startOfDay, subMonths } from 'date-fns'
import { PiCurrencyDollar, PiTrophy, PiTrendUp } from 'react-icons/pi'
import { TargetArrowIcon } from '../Layout/LeftNav/nav-icons'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { cn } from '../UI/cn'
import { TabPanel } from '../FbUI/PillTabs'
import { UnderlineTabs } from '../FbUI/UnderlineTabs'
import { MetricCard } from '../FbUI/MetricCard'
import {
  getHomepageStats,
  getPerformanceStats,
  getPipelineValueStats,
  type HomepageStats,
  type PerformanceStats,
  type PerformanceRange,
  type PipelineValueStats,
} from '../../services/recommendations_api'
import {
  formatInteger,
  formatPercentage,
  formatCurrency,
} from '../../utils/formatting'
import useApiService from '../../services/api'
import { usePreferences } from '../../context/preferences/PreferencesContext'
import {
  DEFAULT_STAGE_COLOR,
  getStageColorSolid,
} from '../SalesStage/stageColors'
import { ProgressChart } from '../NewCompanyPage/Sidebar/shared/ProgressChart'
import { PerformanceChart } from './PerformanceChart'
import type { PeriodType } from './PeriodToggle'
import { RangeSelector } from './RangeSelector'
import type { DateRangeValue } from '../../pages/app/deals/DateRangePicker'

const PERF_TAB_ID = 'performance-dashboard'

/** Shared card container styles used across homepage dashboard cards. */
const CARD_CONTAINER_STYLES =
  'bg-surface rounded-lg border border-line shadow-sm p-4'

interface PerformanceDashboardCardProps {
  /** Optional user ID for superusers to view another user's performance. */
  userId?: number
  /** Optional additional CSS class names. */
  className?: string
}

/**
 * Skeleton loader for the performance dashboard.
 *
 * Mirrors the exact layout of the loaded card to prevent content shift:
 * header → 4 metric cards → chart tabs + bar chart area.
 */
const PerformanceDashboardSkeleton: React.FC<{ className?: string }> = ({
  className,
}) => (
  <div className={cn(CARD_CONTAINER_STYLES, className)}>
    <div className="animate-pulse">
      {/* Header: title + subtitle (left), range selector pill (right) */}
      <div className="flex items-center justify-between mb-4">
        <div className="space-y-2">
          <div className="h-6 w-32 bg-surface-disabled rounded" />
          <div className="h-4 w-44 bg-surface-raised rounded" />
        </div>
        <div className="h-8 w-32 bg-surface-disabled rounded" />
      </div>

      {/* Section tabs */}
      <div className="flex gap-2 border-b border-line pb-2 mb-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-6 w-24 bg-surface-disabled rounded" />
        ))}
      </div>

      {/* 4 MetricCards: icon + bold number + label */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className="bg-surface rounded-lg border border-line p-3 text-center"
          >
            {/* Icon row */}
            <div className="flex items-center justify-center mb-1">
              <div className="h-4 w-4 bg-surface-disabled rounded" />
            </div>
            {/* Value */}
            <div className="h-7 w-12 bg-surface-disabled rounded mx-auto mb-1" />
            {/* Label */}
            <div className="h-3 w-16 bg-surface-raised rounded mx-auto" />
          </div>
        ))}
      </div>

      {/* Chart section: "MONTHLY TRENDS" label + tabs + chart area */}
      <div>
        <div className="h-3 w-28 bg-surface-disabled rounded mb-2" />
        {/* Tab row: Deals | Emails | Value */}
        <div className="flex gap-1 mb-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-7 w-14 bg-surface-disabled rounded" />
          ))}
        </div>
        {/* Bar chart area — matches h-56 */}
        <div className="h-56 bg-surface-disabled rounded" />
      </div>
    </div>
  </div>
)

/**
 * Performance dashboard showing historical metrics over time.
 *
 * Layout:
 * 1. Header with range selector and period toggle (Monthly/Weekly)
 * 2. Summary metrics for selected range (Deals Opened, Won, Win Rate, Value Opened, Value Won, Emails Sent)
 * 3. Bar chart with tabs (Deals, Emails, Value) showing trends over the selected time window
 */
export const PerformanceDashboardCard: React.FC<
  PerformanceDashboardCardProps
> = ({ userId, className }) => {
  const [periodType] = useState<PeriodType>('monthly')
  const [range, setRange] = useState<PerformanceRange>('6m')
  // Explicit start/end window, only meaningful when `range === 'custom'`.
  const [customRange, setCustomRange] = useState<DateRangeValue>(undefined)

  // A custom range is only fetchable once both ends are picked: the backend
  // rejects a custom request missing start_date/end_date. Until then we leave
  // the previously-loaded data in place rather than firing an invalid request.
  const customStart =
    range === 'custom' && customRange?.from
      ? startOfDay(customRange.from).toISOString()
      : undefined
  const customEnd =
    range === 'custom' && customRange?.to
      ? endOfDay(customRange.to).toISOString()
      : undefined
  const isCustomIncomplete = range === 'custom' && (!customStart || !customEnd)

  // Switching to "custom" with no window yet seeds a sensible default (the
  // last 3 months) so all three sections immediately reflect a valid range
  // and the calendar pill opens pre-populated; the user can then narrow it.
  const handleRangeChange = (next: PerformanceRange) => {
    if (next === 'custom' && !customRange?.from) {
      const to = new Date()
      // subMonths handles month-end rollover safely (e.g. seeding from the
      // 31st), unlike a raw setMonth which can skip into the wrong month.
      const from = subMonths(to, 3)
      setCustomRange({ from, to })
    }
    setRange(next)
  }
  // Three tabs swap between the headline summary, the pipeline-by-
  // stage breakdown, and the trends chart. Summary is the default —
  // it's the at-a-glance view most users land on the home page for.
  type SectionTab = 'summary' | 'pipeline' | 'trends'
  const [activeSection, setActiveSection] = useState<SectionTab>('summary')

  // Pipeline-by-stage breakdown — sourced from the homepage-stats endpoint.
  // Same query key as the (now-removed) PipelineSnapshotCard so any other
  // caller of getHomepageStats reuses the cache entry.
  const { data: pipelineStats } = useQuery<HomepageStats | null>({
    queryKey: ['homepage-stats', userId ?? 'self'],
    queryFn: () => getHomepageStats(userId),
  })

  // Sales-stage list + custom-order preference drive the per-stage fill
  // color, so the pipeline chart matches the DS2026 phase palette used on
  // stage pills, the deal detail page, and the Kanban board.
  const { data: allStages } = useApiService().useGetSalesStages()
  const { isSettingEnabled } = usePreferences()
  const customOrderEnabled = isSettingEnabled('sales_stage_custom_order')

  const pipelineChartItems = useMemo(() => {
    if (!pipelineStats) return []
    const stagesWithDeals = pipelineStats.deals_by_stage.filter(
      (s) => s.count > 0
    )
    const totalDeals = stagesWithDeals.reduce((sum, s) => sum + s.count, 0)
    return stagesWithDeals.map((stage) => {
      const fullStage = allStages?.find((s) => s.id === stage.stage_id)
      return {
        name: `${stage.stage_name} · ${formatCurrency(stage.total_value)} · ${stage.count} deal${stage.count !== 1 ? 's' : ''}`,
        value: stage.count,
        color:
          fullStage && allStages
            ? getStageColorSolid(fullStage, allStages, customOrderEnabled)
            : DEFAULT_STAGE_COLOR,
        percentage:
          totalDeals > 0 ? Math.round((stage.count / totalDeals) * 100) : 0,
      }
    })
  }, [pipelineStats, allStages, customOrderEnabled])
  // Shared query params + enable gate. A custom range isn't fetchable until
  // both ends are picked (the backend rejects a partial window), so while the
  // user is mid-selection the queries stay disabled and no request fires.
  const statsQueryParams = {
    userId,
    periodType,
    range,
    startDate: customStart,
    endDate: customEnd,
  }
  const statsQueryKey = [
    userId ?? 'self',
    periodType,
    range,
    customStart ?? null,
    customEnd ?? null,
  ]

  // React Query (not effect-based fetching) so a range change refetches in the
  // background while the previously-loaded data stays on screen via
  // `keepPreviousData`. Without it, every edit would unmount the card into the
  // skeleton, closing the open calendar popover (the "flash").
  const {
    data: stats,
    isLoading,
    error: statsError,
  } = useQuery<PerformanceStats | null>({
    queryKey: ['performance-stats', ...statsQueryKey],
    queryFn: () => getPerformanceStats(statsQueryParams),
    enabled: !isCustomIncomplete,
    placeholderData: keepPreviousData,
  })

  // Pipeline value stats are fetched independently so a failure doesn't break
  // the rest of the dashboard — the Pipeline tab simply won't appear.
  const { data: pipelineValueStats } = useQuery<PipelineValueStats | null>({
    queryKey: ['pipeline-value-stats', ...statsQueryKey],
    queryFn: () => getPipelineValueStats(statsQueryParams),
    enabled: !isCustomIncomplete,
    placeholderData: keepPreviousData,
  })

  if (isLoading) {
    return <PerformanceDashboardSkeleton className={className} />
  }

  if (statsError) {
    const message =
      statsError instanceof Error
        ? statsError.message
        : 'Failed to load performance data'
    return (
      <div className={cn(CARD_CONTAINER_STYLES, 'p-6', className)}>
        <div className="text-center text-fg-secondary">
          <p>Unable to load performance data</p>
          <p className="text-sm text-fg-muted mt-1">{message}</p>
        </div>
      </div>
    )
  }

  if (!stats) {
    return null
  }

  const { totals, range_info, periods } = stats

  const performanceMetrics = [
    {
      key: 'deals-opened',
      label: 'Deals Opened',
      icon: <PiTrendUp size={16} />,
      value: formatInteger(totals.deals_opened),
      tooltipContent: `Number of deals opened in ${range_info.range_label.toLowerCase()}`,
    },
    {
      key: 'deals-won',
      label: 'Deals Won',
      icon: <PiTrophy size={16} />,
      value: formatInteger(totals.deals_won),
      tooltipContent: `Deals closed won in ${range_info.range_label.toLowerCase()}`,
    },
    {
      key: 'value-won',
      label: 'Value Won',
      icon: <PiCurrencyDollar size={16} />,
      value: formatCurrency(totals.value_won),
      tooltipContent: `Annual value of deals won in ${range_info.range_label.toLowerCase()}`,
    },
    {
      key: 'win-rate',
      label: 'Win Rate',
      icon: <TargetArrowIcon size={16} />,
      value:
        totals.overall_win_rate !== null
          ? `${formatPercentage(totals.overall_win_rate)}%`
          : '-',
      tooltipContent: `Win rate for ${range_info.range_label.toLowerCase()} (Won / (Won + Lost))`,
    },
  ]

  return (
    <div className={cn(CARD_CONTAINER_STYLES, className)}>
      {/* Title row — mirrors the Action Center header (title + subtitle on the
          left, the range selector as the card-level control on the right). */}
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-semibold text-fg">Performance</h2>
          <p className="mt-0.5 text-base text-fg-secondary">
            Here&apos;s how you&apos;re tracking
          </p>
        </div>
        <div className="flex-shrink-0">
          <RangeSelector
            value={range}
            onChange={handleRangeChange}
            customRange={customRange}
            onCustomRangeChange={setCustomRange}
          />
        </div>
      </div>

      {/* Section tabs — switch between the stats summary, the pipeline-by-stage
          breakdown, and the trends chart. Pipeline tab is suppressed when
          empty. */}
      <div className="mb-4">
        <UnderlineTabs<SectionTab>
          aria-label="Performance dashboard sections"
          idPrefix={PERF_TAB_ID}
          size="sm"
          className="w-fit"
          activeKey={activeSection}
          onChange={setActiveSection}
          tabs={[
            { key: 'summary', title: 'Your Stats' },
            ...(pipelineChartItems.length > 0
              ? ([{ key: 'pipeline', title: 'Pipeline by stage' }] as const)
              : []),
            {
              key: 'trends',
              title: `${periodType === 'monthly' ? 'Monthly' : 'Weekly'} Trends`,
            },
          ]}
        />
      </div>

      <TabPanel<SectionTab>
        idPrefix={PERF_TAB_ID}
        tabKey="summary"
        activeKey={activeSection}
        className="grid grid-cols-4 gap-3"
      >
        {performanceMetrics.map((metric) => (
          <MetricCard
            key={metric.key}
            icon={metric.icon}
            value={metric.value}
            label={metric.label}
            tooltipContent={metric.tooltipContent}
          />
        ))}
      </TabPanel>

      {pipelineChartItems.length > 0 && (
        <TabPanel<SectionTab>
          idPrefix={PERF_TAB_ID}
          tabKey="pipeline"
          activeKey={activeSection}
        >
          <ProgressChart
            items={pipelineChartItems}
            tooltipValueFormatter={(item) =>
              `${item.value} deal${item.value !== 1 ? 's' : ''}`
            }
            legend={{
              // No title — the "Pipeline by stage" tab already names this view,
              // and the sibling tabs don't repeat their name inside the panel.
              columns: 2,
              showPercentages: true,
            }}
            bareLegend
          />
        </TabPanel>
      )}

      <TabPanel<SectionTab>
        idPrefix={PERF_TAB_ID}
        tabKey="trends"
        activeKey={activeSection}
      >
        <PerformanceChart
          periods={periods}
          pipelinePeriods={pipelineValueStats?.periods}
        />
      </TabPanel>
    </div>
  )
}
